export const CHECK_STATUS = {
  PASSED: 'passed',
  FLAGGED: 'flagged',
  NOT_EVALUATED: 'not_evaluated'
}

const ORDER = { flagged: 0, not_evaluated: 1, passed: 2 }

const SCOPE_TEXT = {
  per_event: 'per event',
  per_annum: 'per year',
  per_policy: 'per policy',
  per_lifetime: 'per lifetime',
  cross_policy: "across the customer's policies"
}

const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key)

const humanise = text => {
  const spaced = String(text)
    .replace(/_/g, ' ')
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function normaliseChecks(checks) {
  if (!Array.isArray(checks)) return []
  const clean = []
  checks.forEach(check => {
    if (!check || typeof check !== 'object') return
    const known = has(ORDER, check.status)
    clean.push({
      code: typeof check.code === 'string' ? check.code : '',
      status: known ? check.status : CHECK_STATUS.NOT_EVALUATED,
      message: typeof check.message === 'string' ? check.message : ''
    })
  })
  return clean
}

export function sortChecks(checks) {
  return checks
    .map((check, index) => ({ check, index }))
    .sort(
      (a, b) =>
        ORDER[a.check.status] - ORDER[b.check.status] || a.index - b.index
    )
    .map(entry => entry.check)
}

export function summariseChecks(checks) {
  const summary = {
    flagged: 0,
    notEvaluated: 0,
    passed: 0,
    total: checks.length
  }
  checks.forEach(check => {
    if (check.status === CHECK_STATUS.FLAGGED) summary.flagged += 1
    else if (check.status === CHECK_STATUS.PASSED) summary.passed += 1
    else summary.notEvaluated += 1
  })
  return summary
}

export function summaryText(summary) {
  const parts = []
  if (summary.flagged) parts.push(`${summary.flagged} flagged for review`)
  if (summary.notEvaluated) parts.push(`${summary.notEvaluated} not checked`)
  if (summary.passed) parts.push(`${summary.passed} passed`)
  return parts.length ? parts.join(', ') : 'No checks recorded'
}

export function checkTitle(code) {
  if (typeof code !== 'string' || !code) return 'Check'
  const parts = code.split(':')
  const kind = parts[0]
  if (kind === 'notification_window' && parts.length === 1) {
    return 'Notification window'
  }
  if (kind === 'claim_config' && parts.length === 1) {
    return 'Claim configuration'
  }
  if (kind === 'exclusion' && parts.length === 2) {
    return `Exclusion: ${humanise(parts[1]).toLowerCase()}`
  }
  if (kind === 'waiting_period' && parts.length === 2) {
    return `Waiting period: ${parts[1]}`
  }
  if (kind === 'limit' && (parts.length === 3 || parts.length === 4)) {
    const scope = has(SCOPE_TEXT, parts[2])
      ? SCOPE_TEXT[parts[2]]
      : humanise(parts[2]).toLowerCase()
    const label =
      parts.length === 4 && parts[3] === 'claim_count'
        ? 'Number of claims: '
        : 'Limit: '
    if (parts.length === 3 || parts[3] === 'claim_count') {
      return `${label + parts[1]} (${scope})`
    }
  }
  return code
}

export function normaliseChecklist(items) {
  if (!Array.isArray(items)) return []
  const clean = []
  items.forEach(item => {
    if (
      !item ||
      typeof item !== 'object' ||
      typeof item.code !== 'string' ||
      !item.code
    ) {
      return
    }
    clean.push({
      code: item.code,
      mandatory: item.mandatory === true,
      alternatives: Array.isArray(item.alternatives)
        ? item.alternatives.filter(a => typeof a === 'string')
        : [],
      conditional: item.applies === 'conditional',
      forEvents: Array.isArray(item.for_events)
        ? item.for_events.filter(e => typeof e === 'string')
        : [],
      satisfied:
        typeof item.satisfied === 'boolean' ? item.satisfied : undefined
    })
  })
  return clean
}

export function documentLabel(code) {
  return humanise(code)
}

export const NOT_LISTED = '__not_listed__'
export const NOT_LISTED_TEXT = 'Not one of the required documents'

export function documentTypeOptions(items) {
  const options = []
  const seen = {}
  const add = (code, mandatory) => {
    if (has(seen, code)) return
    seen[code] = true
    const label = documentLabel(code)
    options.push({
      code,
      label,
      mandatory,
      text: mandatory ? `${label} (required)` : label
    })
  }
  items.forEach(item => add(item.code, item.mandatory))
  items.forEach(item => item.alternatives.forEach(code => add(code, false)))
  return options
}

export function conditionText(item) {
  return item.forEvents.length > 0
    ? `only for ${item.forEvents
        .map(e => humanise(e).toLowerCase())
        .join(', ')}`
    : 'only in some cases'
}

const CARRIED = [
  'required_documents_checklist',
  'claim_checks',
  'required_documents_enforced'
]

export function mergeUploadedClaim(claim, added) {
  const refreshed = { claim_documents: added.claim_documents }
  CARRIED.forEach(key => {
    if (added[key] !== undefined) refreshed[key] = added[key]
  })
  return Object.assign({}, claim, refreshed)
}

export function documentState(item) {
  if (item.satisfied === true) return 'provided'
  if (item.satisfied === false) return 'not_matched'
  return 'unknown'
}

export function hasAnythingToShow(claim) {
  if (!claim) return false
  return (
    normaliseChecklist(claim.required_documents_checklist).length > 0 ||
    normaliseChecks(claim.claim_checks).length > 0
  )
}
